/* The two panels that decide what a reconciliation does when nobody is watching, and the history of
 * both. Pure functions over wire types, so the screen holds no judgement of its own.
 *
 * The distinctions here are the ones the API went out of its way to keep, and a panel that collapses
 * any of them undoes that work:
 *
 * * **A default is not a decision.** `declared: false` still opens an incident at `warning` on the
 *   first break — the mechanism ships on, not off — but nobody at this tenant chose it. Rendering
 *   the two identically is how a tenant comes to believe a threshold was set that nobody set.
 * * **`last_outcome: null` is not an outcome.** It means the schedule has never come due. A badge
 *   reading "never" beside four real outcomes would make "we have not tried" look like a result.
 * * **A deferral is healthy and a refusal is not.** Five schedule outcomes are five sentences; the
 *   whole reason the backend keeps them apart is that collapsing any pair makes a schedule that is
 *   working-as-configured indistinguishable from one quietly doing nothing.
 */

import type { AuditEntry } from '@/shared/api/generated/audit';
import type {
  BreakPolicy,
  BreakSeverity,
  RunSchedule,
  ScheduleOutcome,
} from '@/shared/api/generated/reconciliation';

import type { ScheduleReading } from '../api/governance';

/* ------------------------------------------------------------------ the break policy */

export type PolicyTone = 'ok' | 'warn' | 'error' | 'muted';

/* The severity an incident opens at, as a tone. `info` is deliberately muted rather than blue-ok:
 * it is the setting that files a record and pages nobody, and painting it as healthy would read as
 * "this is fine" when what it means is "this is recorded". */
const SEVERITY_TONES = {
  info: 'muted',
  warning: 'warn',
  critical: 'error',
} satisfies Record<BreakSeverity, PolicyTone>;

export function severityTone(severity: BreakSeverity): PolicyTone {
  return SEVERITY_TONES[severity];
}

/** Whether this policy is the tenant's or the platform's, which the badge above it reports. */
export function isPlatformDefault(policy: BreakPolicy): boolean {
  return !policy.declared;
}

/** The headline: what happens on a disagreement, in one sentence.
 *
 * Three shapes rather than one with substitutions, because "opens nothing" is a different statement
 * from "opens a warning after five" and a single message with a `{count}` would have to read as the
 * second even when the first is true.
 */
export interface PolicyReading {
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
  readonly tone: PolicyTone;
}

export function policyHeadline(policy: BreakPolicy): PolicyReading {
  if (!policy.enabled) {
    return { key: 'reconciliation.policy.headline.off', values: {}, tone: 'muted' };
  }
  return {
    key:
      policy.min_breaks === 1
        ? 'reconciliation.policy.headline.first'
        : 'reconciliation.policy.headline.threshold',
    values: { severity: policy.severity, count: policy.min_breaks },
    tone: severityTone(policy.severity),
  };
}

/** The sentence under the headline: what the threshold means for a run below it. */
export function policyBodyKey(policy: BreakPolicy): string {
  if (!policy.enabled) return 'reconciliation.policy.body.off';
  return policy.min_breaks === 1
    ? 'reconciliation.policy.body.first'
    : 'reconciliation.policy.body.threshold';
}

/* ------------------------------------------------------------------ the run schedule */

export function isConfigured(reading: ScheduleReading): reading is RunSchedule {
  return reading.configured;
}

export type ScheduleBadge = 'enabled' | 'disabled' | 'suspended';

/** Which of three states the schedule is in, checked in the order that makes each one true.
 *
 * **Suspension is tested first**, because a suspended schedule is still `enabled` — the backend
 * suspends by setting a timestamp rather than by switching the schedule off, precisely so it resumes
 * by itself. Reading `enabled` first would paint a schedule that has stopped trying as running
 * normally, which is the one state an operator most needs to see.
 */
export function scheduleBadge(schedule: RunSchedule): ScheduleBadge {
  if (schedule.suspended_until !== null) return 'suspended';
  return schedule.enabled ? 'enabled' : 'disabled';
}

export type OutcomeTone = 'ok' | 'info' | 'warn' | 'error' | 'muted';

/* Five outcomes, five tones, and the two that matter most are `deferred` and `refused`.
 *
 * `deferred` is **info, not warn**: a run was already in flight, which is the configuration working
 * and the moment being wrong. Nobody acts on it. `refused` is warn because somebody must — the
 * platform declined and the next occurrence will decline identically. `failed` is error because it
 * is ours. `skipped` is muted: the definition is gone, so there is nothing to fix here.
 */
const OUTCOME_TONES = {
  requested: 'ok',
  deferred: 'info',
  refused: 'warn',
  failed: 'error',
  skipped: 'muted',
} satisfies Record<ScheduleOutcome, OutcomeTone>;

export function outcomeTone(outcome: ScheduleOutcome): OutcomeTone {
  return OUTCOME_TONES[outcome];
}

export function outcomeLabelKey(outcome: ScheduleOutcome): string {
  return `reconciliation.schedule.outcome.${outcome}`;
}

/** Monday is 0, matching `date.weekday()` and the stored array. The labels are copy keys so a
 * translator is not handed a hard-coded English day. */
export const WEEKDAY_KEYS: readonly string[] = [
  'reconciliation.weekday.mon',
  'reconciliation.weekday.tue',
  'reconciliation.weekday.wed',
  'reconciliation.weekday.thu',
  'reconciliation.weekday.fri',
  'reconciliation.weekday.sat',
  'reconciliation.weekday.sun',
];

/** The IANA zones offered. **Names only, never offsets**, and that is the substrate's rule rather
 * than a shortlist: an offset like `+05:30` cannot express a DST transition, so a schedule written
 * with one drifts by an hour twice a year while reading as correct. The API refuses an offset. */
export const TIMEZONES: readonly string[] = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Frankfurt',
  'America/New_York',
  'UTC',
];

/** `HH:MM` on a 24-hour clock, which is what the API stores and what the form must produce. */
const LOCAL_TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/u;

export interface ScheduleDraft {
  readonly enabled: boolean;
  readonly cadence: 'daily' | 'weekly';
  readonly localTime: string;
  readonly timezone: string;
  readonly weekdays: readonly number[];
}

/** What is wrong with a schedule draft, or `null`. One problem at a time, in the order somebody
 * fixes them. */
export function scheduleProblemKey(draft: ScheduleDraft): string | null {
  if (!LOCAL_TIME.test(draft.localTime)) return 'reconciliation.schedule.error.time';
  if (!TIMEZONES.includes(draft.timezone))
    return 'reconciliation.schedule.error.timezone';
  /* A weekly cadence with no day selected is a schedule that runs on no day while claiming to be
   * weekly. A *daily* cadence still sends all seven, which the caller supplies rather than the
   * person choosing — the domain and a CHECK both require a non-empty set. */
  if (draft.cadence === 'weekly' && draft.weekdays.length === 0) {
    return 'reconciliation.schedule.error.weekdays';
  }
  return null;
}

/** Every day for `daily`, the chosen set for `weekly`.
 *
 * The seven are filled in here rather than left to the person, because "daily" and "every weekday
 * ticked" are the same schedule and asking somebody to tick seven boxes to express the first is a
 * form inventing work. */
export function weekdaysFor(draft: ScheduleDraft): readonly number[] {
  return draft.cadence === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : [...draft.weekdays].sort();
}

/* ------------------------------------------------------------------ the history panel */

export type HistoryTone = 'ok' | 'info' | 'warn' | 'error' | 'muted';

/* The chain actions a definition accumulates, and the tone each reads at. Derived from the action
 * string rather than from a second list of "interesting" actions, so an action this map does not
 * know renders rather than vanishing — a history that silently drops rows is worse than one showing
 * an unfamiliar verb, because a reader cannot tell the difference between nothing happening and
 * nothing being displayed. */
const HISTORY_TONES: Readonly<Record<string, HistoryTone>> = {
  'reconciliation.created': 'ok',
  'reconciliation.tolerances_published': 'info',
  'reconciliation.break_policy_configured': 'info',
  'run_schedule.configured': 'info',
  'run_schedule.requested': 'ok',
  'run_schedule.deferred': 'info',
  'run_schedule.refused': 'warn',
  'run_schedule.failed': 'error',
  'run_schedule.skipped': 'muted',
};

export interface HistoryRow {
  readonly key: string;
  readonly occurredAt: string;
  readonly action: string;
  readonly actor: string;
  readonly tone: HistoryTone;
}

export function historyRows(entries: readonly AuditEntry[]): readonly HistoryRow[] {
  return entries.map((entry) => ({
    /* `chain_date` and `sequence` together, because `sequence` restarts at 1 each day — a key of
     * the sequence alone collides across days and React reuses the wrong row. */
    key: `${entry.chain_date}#${String(entry.sequence)}`,
    occurredAt: entry.occurred_at,
    action: entry.action,
    actor: entry.actor,
    tone: HISTORY_TONES[entry.action] ?? 'muted',
  }));
}

/** Whether an actor is a timer rather than a person, which the panel renders differently.
 *
 * `system:` is the platform's own prefix for an unattended sweep, and a scheduled reconciliation's
 * entries carry `system:reconciliation-scheduler`. Showing that as a name would put a string nobody
 * can be asked about in the column a reader scans for accountability.
 */
export function isSystemActor(actor: string): boolean {
  return actor.startsWith('system:');
}
